// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// #124 react-joyride Time Machine tutorial — step definitions.
//
// Pure data. Each step is `{ target, title, content, placement,
// skipBeacon }` per the react-joyride v3 API. The tour driver reads
// this array and walks through it; the driver itself performs every
// state change between steps (entering time-travel, toggling Diff
// Pair, exiting at the end) so the user only ever clicks Next / Back
// in the tour bubble.
//
// Language-aware from day 1 (matches manualIndex.js shape) — only the
// "en" entry is populated for v1; Spanish slots in later by populating
// TOUR_STEPS.es.
//
// Step targets reference either `data-tutorial-target` (added in
// Phase 4) or existing `data-testid` (reused for elements with
// semantic test coverage already).

const TOUR_STEPS = {
  en: [
    {
      // Step 0 — Live mode. Tour driver will enter time-travel when
      // the user clicks Next.
      target: '[data-tutorial-target="time-machine-button"]',
      title: "🕒 Time Travel",
      content:
        "This is the Time Travel button. It lets you see the schedule exactly as it looked at any past moment. Click Next and the tour will enter Time Travel for you.",
      placement: "bottom",
      skipBeacon: true,
    },
    {
      // Step 1 — Now in TT, Diff Pair is on by default.
      target: '[data-tutorial-target="time-machine-banner"]',
      title: "The sepia banner",
      content:
        "The header has been replaced by this sepia banner. While the banner is up, every part of the app is read-only — you cannot change anything by accident.",
      placement: "bottom",
      skipBeacon: true,
    },
    {
      // Step 2 — Day picker. Visible in any mode.
      target: '[data-tutorial-target="day-picker"]',
      title: "Day navigation",
      content:
        "Use these arrows to step through days. The Today button snaps back. The arrows grey out at the historical floor and at today.",
      placement: "bottom",
      skipBeacon: true,
    },
    {
      // Step 3 — Time slider. Banner-level slider only visible when
      // Diff Pair view is OFF. Tour driver toggles it off before
      // advancing INTO this step. placement=bottom so the bubble
      // sits BELOW the slider (in the grid area) instead of trying
      // to overflow upward into the banner row above.
      target: '[data-tutorial-target="time-slider"]',
      title: "Scrubbing through the day",
      content:
        "Drag this slider to scrub through the day. Each tick is a real recorded event. The arrows on either side jump to the previous or next event.",
      placement: "bottom",
      skipBeacon: true,
    },
    {
      // Step 4 — Return to Live preview. Visible in any mode.
      target: '[data-tutorial-target="return-to-live"]',
      title: "Exiting time-travel",
      content:
        "When you're done exploring history, this button takes you back to live mode. The tour will exit for you at the end — don't click it yet.",
      placement: "bottom",
      skipBeacon: true,
    },
    {
      // Step 5 — Diff Pair split-screen. Driver toggles Diff Pair on
      // before advancing INTO this step. placement=center because
      // the diff-pair container is most of the viewport; no
      // particular edge makes sense to anchor a bubble to.
      target: '[data-tutorial-target="diff-pair-view"]',
      title: "Diff Pair view",
      content:
        "On the Optimizer, Time Travel can split the schedule into two grids side-by-side. The left grid shows the start of the day; the right shows the end. Everything that changed in between is highlighted in red on the right.",
      placement: "center",
      skipBeacon: true,
    },
    {
      // Step 6 — Red user bubbles on changed tiles.
      //
      // (#133 fix) Targets the Diff Pair container with placement="center"
      // — same pattern as step 5 — instead of spotlighting a specific
      // user-bubble. The user-bubble selector only matches when the
      // current Time Travel date has at least one diff between T1 and T2
      // for the user's location; on quiet days the target doesn't exist
      // and react-joyride hunts erratically. Additionally, when the
      // target DOES exist, react-joyride's scroll-to-target nudges only
      // the right grid (whose scrollbar is hidden) and Diff Pair's
      // sync-scroll handler is left→right only, so the grids desync.
      // Center placement on the parent container avoids both failure
      // modes. The user learns what bubbles MEAN and where to look,
      // even when no bubbles are present on the current date.
      target: '[data-tutorial-target="diff-pair-view"]',
      title: "Who did what",
      content:
        "When the schedule changes during a day, small RED BUBBLES appear above the affected tiles in the right grid — each bubble shows the name of the person who made the change. Hover for the full email and exact timestamp. If multiple people touched the same tile, the bubbles stack. (You may not see any bubbles on the current day if nothing was changed — try a busier date in real use to see them in action.)",
      placement: "center",
      skipBeacon: true,
    },
    {
      // Step 7 — Add Grid toggle. Tour-end step; driver exits TT
      // and marks the tutorial complete after Next.
      target: '[data-tutorial-target="add-grid-toggle"]',
      title: "Single vs. split view",
      content:
        "This button toggles between the split-screen Diff Pair and a single grid. Tip: in regular Time Travel use, click any job tile to see its full History feed, and click any crew name for the Crew History modal — these are the forensic tools when you need 'who changed this and when?'\n\nClick Next to finish the tour and return to live.",
      placement: "bottom",
      skipBeacon: true,
    },
  ],
  es: [],
};

const DEFAULT_LANGUAGE = "en";

/**
 * Returns the tour-step array for the requested language, falling back
 * to DEFAULT_LANGUAGE if the language has no content yet.
 */
function getTourSteps(language) {
  const steps = TOUR_STEPS[language];
  if (Array.isArray(steps) && steps.length > 0) return steps;
  return TOUR_STEPS[DEFAULT_LANGUAGE];
}

export { TOUR_STEPS, DEFAULT_LANGUAGE, getTourSteps };
